import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowRight, User, Printer, Edit, Trash2, Share2, Plus, ChevronDown, ChevronUp } from 'lucide-react'
import useInvoiceStore from '../../stores/useInvoiceStore'
import useCustomerStore from '../../stores/useCustomerStore'
import usePermissions from '../../hooks/usePermissions'
import StatusBadge from '../../components/invoices/StatusBadge'
import StatusChangeModal from '../../components/invoices/StatusChangeModal'
import PaymentStatusBadge from '../../components/invoices/PaymentStatusBadge'
import AddPaymentModal from '../../components/invoices/AddPaymentModal'
import PaymentHistoryList from '../../components/invoices/PaymentHistoryList'
import PrintableInvoice from '../../components/invoices/PrintableInvoice'
import Toast from '../../components/common/Toast'

/**
 * Invoice Details Page Component
 * عرض تفاصيل الفاتورة كاملة
 *
 * Features:
 * - Display complete invoice information
 * - Customer details
 * - Line items table
 * - Totals calculation
 * - Action buttons (Edit, Delete, Print)
 *
 * Protocols Applied:
 * - P-YNEX-COMP-002: Component Naming Convention
 * - P-YNEX-CSS-001: Tailwind CSS Usage
 */
const InvoiceDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const permissions = usePermissions()

  const {
    selectedInvoice,
    isLoadingDetails,
    isLoadingPayments,
    error,
    getInvoiceById,
    updateInvoiceStatus,
    deleteInvoice,
    addPayment,
    fetchPayments,
    getPaymentsByInvoice,
    getTotalPaid,
    getRemainingBalance,
    getPaymentStatus
  } = useInvoiceStore()

  // Customer store for refreshing data after payment
  const { fetchCustomers } = useCustomerStore()
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false)
  const [showPaymentHistory, setShowPaymentHistory] = useState(true)
  const [toast, setToast] = useState(null)

  // Load invoice and payments on mount
  // getInvoiceById and fetchPayments are stable functions from Zustand store
  useEffect(() => {
    if (id) {
      getInvoiceById(id)
      fetchPayments(id) // جلب الدفعات من الـ API
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Handle status change
  const handleStatusChange = async (newStatus) => {
    const result = await updateInvoiceStatus(id, newStatus)
    if (result.success) {
      setToast({ type: 'success', message: result.message })
      setShowStatusModal(false)
    } else {
      setToast({ type: 'error', message: result.error || 'فشل في تحديث الحالة' })
    }
  }

  // Handle delete invoice
  const handleDelete = async () => {
    const result = await deleteInvoice(id)
    if (result.success) {
      setToast({ type: 'success', message: result.message })
      setTimeout(() => navigate('/invoices'), 1500)
    } else {
      setToast({ type: 'error', message: result.error || 'فشل في حذف الفاتورة' })
    }
  }

  // Handle add payment
  const handleAddPayment = async (paymentData) => {
    const result = await addPayment(id, paymentData)
    if (result.success) {
      setToast({ type: 'success', message: result.message })
      setShowAddPaymentModal(false)
      // Refresh invoice to get updated payment status
      getInvoiceById(id)
      // Refresh customers data to update credit limit display
      fetchCustomers()
    } else {
      setToast({ type: 'error', message: result.error || 'فشل في إضافة الدفعة' })
    }
  }

  // Handle print invoice
  const handlePrint = () => {
    window.print()
  }

  // Get payment data
  const payments = selectedInvoice ? getPaymentsByInvoice(id) : []
  const totalPaid = selectedInvoice ? getTotalPaid(id) : 0
  const remainingBalance = selectedInvoice ? getRemainingBalance(id) : 0
  const paymentStatus = selectedInvoice ? getPaymentStatus(id) : 'unpaid'

  // Loading state
  if (isLoadingDetails) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-700 dark:text-gray-300">جاري تحميل الفاتورة...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="card p-6 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">حدث خطأ</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => navigate('/invoices')}
            className="btn btn-primary"
          >
            العودة للقائمة
          </button>
        </div>
      </div>
    )
  }

  // Invoice not found
  if (!selectedInvoice) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="card p-6 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">لم يتم العثور على الفاتورة</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">الفاتورة #{id} غير موجودة</p>
          <button
            onClick={() => navigate('/invoices')}
            className="btn btn-primary"
          >
            العودة للقائمة
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/invoices')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="رجوع"
          >
            <ArrowRight className="w-6 h-6 text-gray-600 dark:text-gray-400" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                فاتورة #{selectedInvoice.invoiceNumber || id}
              </h1>
              <div
                onClick={() => setShowStatusModal(true)}
                className="cursor-pointer hover:opacity-80 transition-opacity"
              >
                <StatusBadge status={selectedInvoice.status} />
              </div>
              {/* Invoice Type Badge */}
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                selectedInvoice.status === 'cash'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
              }`}>
                {selectedInvoice.status === 'cash' ? '💵 كاش' : '📋 آجل'}
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {new Date(selectedInvoice.createdAt).toLocaleDateString('ar-KW', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="px-4 py-4 space-y-4">
        {/* Customer Info Card */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            معلومات العميل
          </h3>
          <div className="flex items-start gap-3 mb-3">
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-gray-900 dark:text-white">
                {selectedInvoice.customer.name}
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {selectedInvoice.customer.customer_code || `#${selectedInvoice.customer.id}`}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1" dir="ltr">
                📱 {selectedInvoice.customer.phone}
              </p>
              {selectedInvoice.customer.email && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1" dir="ltr">
                  ✉️ {selectedInvoice.customer.email}
                </p>
              )}
              {selectedInvoice.customer.address && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  📍 {selectedInvoice.customer.address}
                </p>
              )}
            </div>
          </div>

          {/* Credit Info */}
          {selectedInvoice.customer.credit_limit > 0 && (
            <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-gray-50 dark:bg-gray-700/50 p-2 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">الحد الائتماني</p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {parseFloat(selectedInvoice.customer.credit_limit).toLocaleString('ar-KW', { style: 'currency', currency: 'KWD' })}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 p-2 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">الرصيد الحالي</p>
                  <p className={`font-semibold ${
                    parseFloat(selectedInvoice.customer.current_balance) > 0
                      ? 'text-orange-600 dark:text-orange-400'
                      : 'text-green-600 dark:text-green-400'
                  }`}>
                    {parseFloat(selectedInvoice.customer.current_balance).toLocaleString('ar-KW', { style: 'currency', currency: 'KWD' })}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Invoice Details Card */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            تفاصيل الفاتورة
          </h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">📅 تاريخ الإنشاء</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {new Date(selectedInvoice.date || selectedInvoice.createdAt).toLocaleDateString('ar-KW', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
              </p>
            </div>

            {selectedInvoice.due_date && (
              <div className={`p-3 rounded-lg ${
                selectedInvoice.is_overdue
                  ? 'bg-red-50 dark:bg-red-900/20'
                  : 'bg-gray-50 dark:bg-gray-700/50'
              }`}>
                <p className={`text-xs mb-1 ${
                  selectedInvoice.is_overdue
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`}>
                  📆 تاريخ الاستحقاق
                </p>
                <p className={`font-medium ${
                  selectedInvoice.is_overdue
                    ? 'text-red-700 dark:text-red-300'
                    : 'text-gray-900 dark:text-white'
                }`}>
                  {new Date(selectedInvoice.due_date).toLocaleDateString('ar-KW', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </p>
                {selectedInvoice.is_overdue && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">⚠️ متأخرة</p>
                )}
              </div>
            )}

            {selectedInvoice.sales_representative && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg col-span-2">
                <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">👤 المندوب</p>
                <p className="font-medium text-blue-900 dark:text-blue-200">
                  {selectedInvoice.sales_representative.name}
                </p>
                {selectedInvoice.sales_representative.code && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                    {selectedInvoice.sales_representative.code}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Line Items Table */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            المنتجات
          </h3>

          {/* Table Header - Hidden on mobile */}
          <div className="hidden md:grid grid-cols-12 gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <div className="col-span-5">المنتج</div>
            <div className="col-span-2 text-center">الكمية</div>
            <div className="col-span-2">السعر</div>
            <div className="col-span-3 text-left">الإجمالي</div>
          </div>

          {/* Line Items */}
          <div className="space-y-2">
            {selectedInvoice.lineItems.map((item) => {
              // Calculate price from total if unit_price is 0 (legacy invoices fix)
              const displayPrice = item.price > 0 ? item.price : (item.total / item.quantity)

              return (
              <div
                key={item.id}
                className="grid grid-cols-12 gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
              >
                <div className="col-span-12 md:col-span-5">
                  <p className="font-medium text-gray-900 dark:text-white">
                    {item.productName}
                  </p>
                  {item.product && (
                    <div className="flex gap-2 mt-1">
                      {item.product.code && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {item.product.code}
                        </span>
                      )}
                      {item.product.unit && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          • {item.product.unit}
                        </span>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 dark:text-gray-400 md:hidden mt-1">
                    {item.quantity} {item.product?.unit || ''} × {displayPrice.toLocaleString('ar-KW', { style: 'currency', currency: 'KWD' })}
                  </p>
                </div>
                <div className="hidden md:block md:col-span-2 text-center">
                  <span className="text-gray-900 dark:text-white">
                    {item.quantity} {item.product?.unit || ''}
                  </span>
                </div>
                <div className="hidden md:block md:col-span-2">
                  <span className="text-gray-900 dark:text-white">
                    {displayPrice.toLocaleString('ar-KW', { style: 'currency', currency: 'KWD' })}
                  </span>
                </div>
                <div className="col-span-12 md:col-span-3 text-left">
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {item.total.toLocaleString('ar-KW', { style: 'currency', currency: 'KWD' })}
                  </span>
                </div>
              </div>
            )})}
          </div>
        </div>

        {/* Totals Section */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            الحسابات
          </h3>

          <div className="space-y-2">
            {/* Subtotal */}
            <div className="flex items-center justify-between py-2">
              <span className="text-gray-700 dark:text-gray-300">الإجمالي الفرعي:</span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {selectedInvoice.subtotal.toLocaleString('ar-KW', { style: 'currency', currency: 'KWD' })}
              </span>
            </div>

            {/* Discount - Only show if there is a discount */}
            {selectedInvoice.discount_amount > 0 && (
              <div className="flex items-center justify-between py-2">
                <span className="text-gray-700 dark:text-gray-300">
                  الخصم ({selectedInvoice.discount_type === 'percentage'
                    ? `${selectedInvoice.discount_value}%`
                    : 'مبلغ ثابت'}):
                </span>
                <span className="font-semibold text-red-600 dark:text-red-400">
                  -{selectedInvoice.discount_amount.toLocaleString('ar-KW', { style: 'currency', currency: 'KWD' })}
                </span>
              </div>
            )}

            {/* Tax */}
            <div className="flex items-center justify-between py-2">
              <span className="text-gray-700 dark:text-gray-300">
                ضريبة القيمة المضافة ({(selectedInvoice.taxRate * 100).toFixed(0)}%):
              </span>
              <span className="font-semibold text-gray-900 dark:text-white">
                {selectedInvoice.tax.toLocaleString('ar-KW', { style: 'currency', currency: 'KWD' })}
              </span>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>

            {/* Total */}
            <div className="flex items-center justify-between py-2">
              <span className="text-lg font-semibold text-gray-900 dark:text-white">
                الإجمالي النهائي:
              </span>
              <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
                {selectedInvoice.total.toLocaleString('ar-KW', { style: 'currency', currency: 'KWD' })}
              </span>
            </div>
          </div>
        </div>

        {/* Payment Status Card - NEW SYSTEM (December 2025) */}
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            حالة الدفع
          </h3>

          <div className={`p-4 rounded-lg ${
            selectedInvoice.status === 'cash'
              ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
              : 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800'
          }`}>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                نوع الفاتورة:
              </span>
              <span className={`font-bold ${
                selectedInvoice.status === 'cash'
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-orange-600 dark:text-orange-400'
              }`}>
                {selectedInvoice.status === 'cash' ? 'مدفوعة بالكامل' : 'آجل (مستحقة)'}
              </span>
            </div>

            {/* Payment Method - Always show if available */}
            {selectedInvoice.payment_method && (
              <div className={`mt-3 pt-3 ${
                selectedInvoice.status === 'cash'
                  ? 'border-t border-green-200 dark:border-green-800'
                  : 'border-t border-orange-200 dark:border-orange-800'
              }`}>
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium ${
                    selectedInvoice.status === 'cash'
                      ? 'text-green-700 dark:text-green-300'
                      : 'text-orange-700 dark:text-orange-300'
                  }`}>
                    طريقة الدفع:
                  </span>
                  <span className={`text-sm font-bold ${
                    selectedInvoice.status === 'cash'
                      ? 'text-green-800 dark:text-green-200'
                      : 'text-orange-800 dark:text-orange-200'
                  }`}>
                    {selectedInvoice.payment_method.name || selectedInvoice.payment_method.name_e || 'نقدي'}
                  </span>
                </div>
              </div>
            )}

            {selectedInvoice.status === 'cash' && (
              <div className="mt-3">
                <p className="text-xs text-green-700 dark:text-green-300">
                  ✅ تم الدفع بالكامل عند إنشاء الفاتورة
                </p>
                <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                  • لا يتم إنشاء سند قبض للفواتير النقدية (دفع مباشر)
                </p>
              </div>
            )}

            {selectedInvoice.status === 'credit' && (
              <div className="mt-3 pt-3 border-t border-orange-200 dark:border-orange-800">
                <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">
                  • يمكن إنشاء سند قبض من صفحة الفواتير الرئيسية
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Notes Section */}
        {selectedInvoice.notes && (
          <div className="card p-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              ملاحظات
            </h3>
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {selectedInvoice.notes}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="card p-4 mb-20">
          {/* Debug logging */}
          {(() => {
            console.log('🔍 [InvoiceDetails] Permissions check:', {
              'selectedInvoice.can_edit': selectedInvoice?.can_edit,
              'selectedInvoice.can_delete': selectedInvoice?.can_delete,
              'permissions.canEdit': permissions.canEdit,
              'permissions.canDelete': permissions.canDelete,
              'Edit button will show': selectedInvoice?.can_edit && permissions.canEdit,
              'Delete button will show': selectedInvoice?.can_delete && permissions.canDelete,
            })
            return null
          })()}

          {/* All Action Buttons - Icon Only in Single Row */}
          <div className="flex items-center justify-center gap-3">
            {/* Print Button */}
            <button
              onClick={handlePrint}
              className="w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 transition-all active:scale-95"
              title="طباعة"
            >
              <Printer className="w-5 h-5" />
            </button>

            {/* Share Button */}
            <button
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: `فاتورة #${selectedInvoice.invoiceNumber}`,
                    text: `فاتورة لـ ${selectedInvoice.customer.name} - ${selectedInvoice.total.toLocaleString('ar-KW', { minimumFractionDigits: 3 })} د.ك`
                  })
                }
              }}
              className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 transition-all active:scale-95"
              title="مشاركة"
            >
              <Share2 className="w-5 h-5" />
            </button>

            {/* Edit Button - Check both invoice and user permissions */}
            {selectedInvoice?.can_edit && permissions.canEdit && (
              <button
                onClick={() => navigate(`/invoices/edit/${id}`)}
                className="w-12 h-12 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 transition-all active:scale-95"
                title="تعديل"
              >
                <Edit className="w-5 h-5" />
              </button>
            )}

            {/* Delete Button - Check both invoice and user permissions */}
            {selectedInvoice?.can_delete && permissions.canDelete && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="w-12 h-12 rounded-full bg-gradient-to-r from-red-500 to-pink-500 text-white flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 transition-all active:scale-95"
                title="حذف"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Status Change Modal */}
      {showStatusModal && (
        <StatusChangeModal
          currentStatus={selectedInvoice.status}
          onConfirm={handleStatusChange}
          onCancel={() => setShowStatusModal(false)}
        />
      )}

      {/* Hidden Printable Invoice (for printing only) */}
      <div className="hidden print:block">
        <PrintableInvoice invoice={selectedInvoice} />
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6 shadow-xl animate-scale-in">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30">
              <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>

            <h3 className="text-lg font-bold text-center mb-2 text-gray-900 dark:text-white">
              تأكيد الحذف
            </h3>
            <p className="text-center text-gray-600 dark:text-gray-400 mb-6">
              هل أنت متأكد من حذف هذه الفاتورة؟ لا يمكن التراجع عن هذا الإجراء.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn btn-secondary flex-1"
              >
                إلغاء
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false)
                  handleDelete()
                }}
                className="btn btn-danger flex-1"
              >
                حذف
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Payment Modal */}
      {showAddPaymentModal && (
        <AddPaymentModal
          invoiceTotal={selectedInvoice.total}
          remainingBalance={remainingBalance}
          onConfirm={handleAddPayment}
          onCancel={() => setShowAddPaymentModal(false)}
        />
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
          duration={3000}
        />
      )}
    </div>
  )
}

export default InvoiceDetails
